import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

interface Sleeve {
  id: number;
  name: string;
  sleeveType: 'EQUITY' | 'FIXED_INCOME' | 'ALTERNATIVES' | 'CASH' | 'CUSTOM';
  modelId?: number;
  modelName?: string;
  targetAllocation: number;
  currentAllocation: number;
  aum: number;
  drift: number;
  managerName?: string;
  strategy?: string;
}

interface UMAAccount {
  id: number;
  accountNumber: string;
  accountName: string;
  householdName: string;
  totalAum: number;
  sleeves: Sleeve[];
  status: 'ACTIVE' | 'PENDING' | 'SUSPENDED';
  createdAt: string;
  lastRebalanced?: string;
}

const Container = styled.div<{ embedded?: boolean }>`
  padding: ${props => props.embedded ? '1.5rem' : '2rem'};
  background: ${props => props.embedded ? 'transparent' : 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'};
  min-height: ${props => props.embedded ? 'auto' : 'calc(100vh - 64px)'};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border: none;
    &:hover {
      background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    }
  ` : `
    background: rgba(255, 255, 255, 0.1);
    color: #e2e8f0;
    border: 1px solid rgba(255, 255, 255, 0.2);
    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  `}
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #94a3b8;
`;

const AccountCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  margin-bottom: 1.5rem;
  overflow: hidden;
`;

const AccountHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const AccountInfo = styled.div``;

const AccountName = styled.h3`
  font-size: 1.125rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 0.25rem 0;
`;

const AccountNumber = styled.span`
  font-size: 0.75rem;
  color: #64748b;
  font-family: monospace;
`;

const AccountMeta = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
`;

const MetaItem = styled.div`
  text-align: right;
`;

const MetaValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #ffffff;
`;

const MetaLabel = styled.div`
  font-size: 0.7rem;
  color: #64748b;
  text-transform: uppercase;
`;

const SleeveGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
  padding: 1.5rem;
`;

const SleeveCard = styled.div<{ hasDrift?: boolean }>`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid ${props => props.hasDrift ? 'rgba(234, 179, 8, 0.3)' : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 12px;
  padding: 1.25rem;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    transform: translateY(-2px);
  }
`;

const SleeveHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const SleeveName = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 0.25rem 0;
`;

const SleeveType = styled.span<{ type: string }>`
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;

  ${props => {
    switch (props.type) {
      case 'EQUITY':
        return 'background: rgba(59, 130, 246, 0.2); color: #3b82f6;';
      case 'FIXED_INCOME':
        return 'background: rgba(34, 197, 94, 0.2); color: #22c55e;';
      case 'ALTERNATIVES':
        return 'background: rgba(168, 85, 247, 0.2); color: #a855f7;';
      case 'CASH':
        return 'background: rgba(148, 163, 184, 0.2); color: #94a3b8;';
      default:
        return 'background: rgba(148, 163, 184, 0.2); color: #94a3b8;';
    }
  }}
`;

const AllocationBar = styled.div`
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.75rem;
`;

const AllocationFill = styled.div<{ percent: number; isOver?: boolean }>`
  height: 100%;
  width: ${props => Math.min(props.percent, 100)}%;
  background: ${props => props.isOver ? '#ef4444' : '#3b82f6'};
  transition: width 0.3s ease;
`;

const AllocationDetails = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: #94a3b8;
`;

const SleeveStats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const SleeveStat = styled.div``;

const SleeveStatValue = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #ffffff;
`;

const SleeveStatLabel = styled.div`
  font-size: 0.65rem;
  color: #64748b;
  text-transform: uppercase;
`;

const DriftIndicator = styled.div<{ severity: 'low' | 'medium' | 'high' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;

  ${props => {
    switch (props.severity) {
      case 'high':
        return 'background: rgba(239, 68, 68, 0.2); color: #ef4444;';
      case 'medium':
        return 'background: rgba(234, 179, 8, 0.2); color: #eab308;';
      default:
        return 'background: rgba(34, 197, 94, 0.2); color: #22c55e;';
    }
  }}
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;

  ${props => {
    switch (props.status) {
      case 'ACTIVE':
        return 'background: rgba(34, 197, 94, 0.2); color: #22c55e;';
      case 'PENDING':
        return 'background: rgba(234, 179, 8, 0.2); color: #eab308;';
      case 'SUSPENDED':
        return 'background: rgba(239, 68, 68, 0.2); color: #ef4444;';
      default:
        return 'background: rgba(148, 163, 184, 0.2); color: #94a3b8;';
    }
  }}
`;

const formatCurrency = (value: number) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const getDriftSeverity = (drift: number): 'low' | 'medium' | 'high' => {
  if (Math.abs(drift) > 5) return 'high';
  if (Math.abs(drift) > 2) return 'medium';
  return 'low';
};

interface SleeveUMAScreenProps {
  embedded?: boolean;
}

const SleeveUMAScreen: React.FC<SleeveUMAScreenProps> = ({ embedded = false }) => {
  const [accounts, setAccounts] = useState<UMAAccount[]>([]);
  const [loading, setLoading] = useState(true);

  // Mock data
  const mockAccounts: UMAAccount[] = [
    {
      id: 1,
      accountNumber: 'UMA-001234',
      accountName: 'Johnson Family UMA',
      householdName: 'Johnson Family',
      totalAum: 2500000,
      status: 'ACTIVE',
      createdAt: '2023-01-15',
      lastRebalanced: '2024-01-10',
      sleeves: [
        {
          id: 1,
          name: 'US Large Cap',
          sleeveType: 'EQUITY',
          modelName: 'Aggressive Growth',
          targetAllocation: 40,
          currentAllocation: 42.5,
          aum: 1062500,
          drift: 2.5,
          managerName: 'Internal',
          strategy: 'Growth',
        },
        {
          id: 2,
          name: 'Fixed Income Core',
          sleeveType: 'FIXED_INCOME',
          modelName: 'Conservative Income',
          targetAllocation: 35,
          currentAllocation: 33.2,
          aum: 830000,
          drift: -1.8,
          managerName: 'Internal',
          strategy: 'Income',
        },
        {
          id: 3,
          name: 'Alternative Strategies',
          sleeveType: 'ALTERNATIVES',
          modelName: 'Multi-Strategy Alt',
          targetAllocation: 15,
          currentAllocation: 16.3,
          aum: 407500,
          drift: 1.3,
          managerName: 'Third Party',
          strategy: 'Alternatives',
        },
        {
          id: 4,
          name: 'Cash Reserve',
          sleeveType: 'CASH',
          targetAllocation: 10,
          currentAllocation: 8.0,
          aum: 200000,
          drift: -2.0,
        },
      ],
    },
    {
      id: 2,
      accountNumber: 'UMA-005678',
      accountName: 'Chen Family Office UMA',
      householdName: 'Chen Family Office',
      totalAum: 8500000,
      status: 'ACTIVE',
      createdAt: '2022-06-20',
      lastRebalanced: '2023-12-15',
      sleeves: [
        {
          id: 5,
          name: 'Global Equity',
          sleeveType: 'EQUITY',
          modelName: 'Global Growth',
          targetAllocation: 50,
          currentAllocation: 56.2,
          aum: 4777000,
          drift: 6.2,
          managerName: 'External - Fidelity',
          strategy: 'Global Growth',
        },
        {
          id: 6,
          name: 'Investment Grade Bonds',
          sleeveType: 'FIXED_INCOME',
          modelName: 'IG Bond Core',
          targetAllocation: 30,
          currentAllocation: 27.8,
          aum: 2363000,
          drift: -2.2,
          managerName: 'Internal',
          strategy: 'Core Plus',
        },
        {
          id: 7,
          name: 'Private Equity',
          sleeveType: 'ALTERNATIVES',
          targetAllocation: 15,
          currentAllocation: 12.5,
          aum: 1062500,
          drift: -2.5,
          managerName: 'External - KKR',
          strategy: 'PE Secondaries',
        },
        {
          id: 8,
          name: 'Cash',
          sleeveType: 'CASH',
          targetAllocation: 5,
          currentAllocation: 3.5,
          aum: 297500,
          drift: -1.5,
        },
      ],
    },
  ];

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await axios.get('/api/uma-accounts');
        setAccounts(response.data);
      } catch (error) {
        console.log('Using mock UMA account data');
        setAccounts(mockAccounts);
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, []);

  const totalAum = accounts.reduce((sum, a) => sum + a.totalAum, 0);
  const totalSleeves = accounts.reduce((sum, a) => sum + a.sleeves.length, 0);
  const accountsNeedingRebalance = accounts.filter(a =>
    a.sleeves.some(s => Math.abs(s.drift) > 5)
  ).length;

  return (
    <Container embedded={embedded}>
      {!embedded && (
        <Header>
          <Title>Sleeve/UMA Management</Title>
          <Button variant="primary">+ Create UMA Account</Button>
        </Header>
      )}

      <StatsGrid>
        <StatCard>
          <StatValue>{accounts.length}</StatValue>
          <StatLabel>UMA Accounts</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{formatCurrency(totalAum)}</StatValue>
          <StatLabel>Total AUM</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{totalSleeves}</StatValue>
          <StatLabel>Active Sleeves</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue style={{ color: accountsNeedingRebalance > 0 ? '#eab308' : '#22c55e' }}>
            {accountsNeedingRebalance}
          </StatValue>
          <StatLabel>Need Rebalancing</StatLabel>
        </StatCard>
      </StatsGrid>

      {accounts.map((account) => (
        <AccountCard key={account.id}>
          <AccountHeader>
            <AccountInfo>
              <AccountName>{account.accountName}</AccountName>
              <AccountNumber>{account.accountNumber} | {account.householdName}</AccountNumber>
            </AccountInfo>
            <AccountMeta>
              <MetaItem>
                <MetaValue>{formatCurrency(account.totalAum)}</MetaValue>
                <MetaLabel>Total AUM</MetaLabel>
              </MetaItem>
              <MetaItem>
                <MetaValue>{account.sleeves.length}</MetaValue>
                <MetaLabel>Sleeves</MetaLabel>
              </MetaItem>
              <StatusBadge status={account.status}>{account.status}</StatusBadge>
              <Button variant="secondary" style={{ padding: '0.5rem 1rem' }}>
                Rebalance
              </Button>
            </AccountMeta>
          </AccountHeader>

          <SleeveGrid>
            {account.sleeves.map((sleeve) => (
              <SleeveCard key={sleeve.id} hasDrift={Math.abs(sleeve.drift) > 5}>
                <SleeveHeader>
                  <div>
                    <SleeveName>{sleeve.name}</SleeveName>
                    {sleeve.modelName && (
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        Model: {sleeve.modelName}
                      </div>
                    )}
                  </div>
                  <SleeveType type={sleeve.sleeveType}>{sleeve.sleeveType.replace('_', ' ')}</SleeveType>
                </SleeveHeader>

                <AllocationBar>
                  <AllocationFill
                    percent={(sleeve.currentAllocation / sleeve.targetAllocation) * 100}
                    isOver={sleeve.currentAllocation > sleeve.targetAllocation}
                  />
                </AllocationBar>

                <AllocationDetails>
                  <span>Target: {sleeve.targetAllocation}%</span>
                  <span>Current: {sleeve.currentAllocation}%</span>
                </AllocationDetails>

                <SleeveStats>
                  <SleeveStat>
                    <SleeveStatValue>{formatCurrency(sleeve.aum)}</SleeveStatValue>
                    <SleeveStatLabel>AUM</SleeveStatLabel>
                  </SleeveStat>
                  <SleeveStat>
                    <DriftIndicator severity={getDriftSeverity(sleeve.drift)}>
                      {sleeve.drift > 0 ? '+' : ''}{sleeve.drift.toFixed(1)}% drift
                    </DriftIndicator>
                  </SleeveStat>
                </SleeveStats>

                {sleeve.managerName && (
                  <div style={{ marginTop: '0.75rem', fontSize: '0.7rem', color: '#64748b' }}>
                    Manager: {sleeve.managerName}
                  </div>
                )}
              </SleeveCard>
            ))}
          </SleeveGrid>
        </AccountCard>
      ))}
    </Container>
  );
};

export default SleeveUMAScreen;
